// @flow

import React from 'react'
import { useEffect, useReducer, useState, useRef } from 'react'
import { Editor } from '@tinymce/tinymce-react'
import { useDropzone } from 'react-dropzone'
import { useDispatch } from 'react-redux'
import { get } from 'lodash-es'
import type { Node } from 'react'
import { useTranslation } from 'react-i18next'
import { getFont, getMailAccountMenuList } from '../core/api/api.mail'
import TrimButton from '../components/Button/Trim'
import styles from './SendMessageForm/SendMessageForm.module.scss'
import {
  NYLAS,
  NYLAS_V3,
} from './Menu/filters/MailsCredentialFilter/MailsCredentialFilter.constants'
import { formatFilesToBase64 } from '../utils/file'
import { globalModalError } from './Layout/Layout.actions'

type Props = {|
  account?: ?Object,
  addLinks?: boolean,
  autoFocus?: boolean,
  autoResizeOnInit: boolean,
  disabled?: boolean,
  imageEnabled?: boolean,
  isOnDrag?: boolean,
  maxHeight?: number,
  onBlur?: () => void,
  onChange: string => void,
  onFocus?: () => void,
  onInit?: (Object, Object) => void,
  placeholder?: string,
  resize: boolean,
  setEditorRef?: Object => void,
  showToolbar: boolean,
  textareaName?: string,
  uuid?: string,
  value: string,
  working?: boolean,
|}

const IMAGE_MAX_SIZE = 20

const REACT_DROPZONE_ERROR_CODES = {
  'file-too-large': 'FileSize',
  'file-invalid-type': 'WrongFileType',
}

const WysiwygEditor = (props: Props): Node => {
  const {
    value,
    placeholder,
    disabled,
    resize,
    autoResizeOnInit,
    maxHeight,
    textareaName,
    showToolbar,
    account,
    working,
    addLinks,
    imageEnabled,
    t,
    uuid,
    autoFocus,
  } = props

  const {
    i18n: { language },
  } = useTranslation()
  const { t: errorT } = useTranslation('Errors')
  const dispatch = useDispatch()
  const editorRef = useRef(null)
  const { open, acceptedFiles, getRootProps, getInputProps, fileRejections } =
    useDropzone({
      noClick: true,
      noKeyboard: true,
      multiple: true,
      maxSize: IMAGE_MAX_SIZE * 1024 * 1024,
      accept: 'image/jpeg,image/png',
      onDrop: () => props.setOnDrag(false),
    })
  const [defaultFont, setDefaultFont] = useState({
    size: 14,
    family: 'sans-serif',
  })
  const [isTrimmed, setIsTrimmed] = useState(false)
  const [isNeedToggleThread, setIsNeedToggleThread] = useState(false)
  const [isLoading, setLoading] = useState(true)
  const [accountId, setAccountId] = useState(null)
  const [onDrag, setOnDrag] = useState(false)
  const [bookmark, setBookmark] = useState(null)

  const [selectKey, forceUpdate] = useReducer(x => x + 1, 0) // hack

  useEffect(() => {
    setOnDrag(props.onDrag)
  }, [props.onDrag])

  useEffect(() => {
    if (!!value && uuid && uuid !== 'create') {
      const { allBlockquoteArray } = getParserAndBlockquote(value)

      if (allBlockquoteArray.length > 1) {
        if (!isNeedToggleThread) {
          setIsNeedToggleThread(true)
          forceUpdate()
        }
      }
    }
  }, [uuid, value])

  useEffect(() => {
    if (onDrag) {
      props.onDragStart?.()
    }
  }, [onDrag])

  useEffect(() => {
    if (isNeedToggleThread) {
      const editor = editorRef?.current?.editor

      if (editor) {
        editor.focus()

        toggleTrim(editor, isTrimmed)
      }
    }
  }, [isNeedToggleThread, isTrimmed])

  useEffect(() => {
    if (!acceptedFiles || !acceptedFiles.length || !editorRef.current?.editor) {
      return
    }

    formatFilesToBase64(acceptedFiles).then(processed => {
      const date = new Date()
      const time = date.getTime()
      processed.forEach((file, i) => {
        const blobCache = editorRef.current.editor.editorUpload.blobCache
        const blobInfo = blobCache.create(
          String(time),
          acceptedFiles[i],
          file.origin
        )

        blobCache.add(blobInfo)

        if (bookmark) {
          editorRef.current.editor.selection.moveToBookmark(bookmark)
        }

        editorRef.current.editor.insertContent(
          editorRef.current.editor.dom.createHTML('img', {
            alt: file.filename,
            src: blobInfo.base64(),
          })
        )
      })
    })
  }, [acceptedFiles])

  useEffect(() => {
    if (fileRejections?.length) {
      let errors = fileRejections.map(f => {
        const error = get(f, ['errors', '0', 'code'])
        const name = get(f, ['file', 'name'], '')

        if (error) {
          return REACT_DROPZONE_ERROR_CODES[error]
            ? errorT(REACT_DROPZONE_ERROR_CODES[error], {
                name,
                size: IMAGE_MAX_SIZE,
              })
            : errorT('File')
        }

        return null
      })

      const errorText = errors.reduce((acc, error) => {
        return acc + error + '<br/>'
      }, '')

      dispatch(globalModalError(errorText, errorT('File')))
    }
  }, [fileRejections])

  const toolbar =
    showToolbar &&
    `fontselect fontsizeselect | bold italic underline forecolor | alignroup | numlist bullist ${
      addLinks ? '| link ' : ''
    }${
      imageEnabled ? '| customImageUploading ' : ''
    } | outdent indent | blockquote removeformat`

  const handleBlur = () => {
    if (props.onBlur) {
      props.onBlur()
    }
  }

  const handleFocusout = () => {
    if (editorRef.current?.editor?.selection) {
      const currentBookmark = editorRef.current.editor.selection.getBookmark()
      setBookmark(currentBookmark)
    }
  }

  const handleFocus = () => {
    if (props.onFocus) {
      props.onFocus()
    }
  }

  const handleInit = (e, editor) => {
    if (props.setEditorRef) {
      props.setEditorRef(editor)
    }

    if (props.onInit) {
      props.onInit(e, editor)
    }
  }

  const handleUpload = () => {
    open()
  }

  const toggleTrim = (editor, isTrim) => {
    const text = editor.getContent()
    const { textParser, allBlockquoteArray } = getParserAndBlockquote(text)
    const display = isTrim ? 'block' : 'none'

    allBlockquoteArray.shift()

    for (const entry of allBlockquoteArray) {
      entry.style.display = display
    }

    editor.setContent(textParser.body.innerHTML)
  }

  const getParserAndBlockquote = (text: string) => {
    const textParser = new DOMParser().parseFromString(text, 'text/html')
    const allBlockquote = textParser.querySelectorAll('blockquote')
    const allBlockquoteArray = Array.from(allBlockquote)

    return { textParser, allBlockquoteArray }
  }

  useEffect(() => {
    if (working) {
      return
    }

    if (account?.id && account.id !== accountId) {
      setAccountId(account.id)
      getMailAccountMenuList({ page_size: 100500, count_unread: 0 })
        .then(data => {
          return [...data.own, ...data.corporate, ...data.shared].filter(
            item =>
              (item.outgoing && item.outgoing.id === account.id) ||
              ((item.account_type === NYLAS ||
                item.account_type === NYLAS_V3) &&
                item.id === account.id)
          )
        })
        .then(data =>
          getFont(data[0].id).then(data => {
            setDefaultFont({ size: data.size.size, family: data.font.family })
            setLoading(false)
            forceUpdate()
          })
        )
    } else {
      setLoading(false)
    }
  }, [account, working])

  if (isLoading) {
    return null
  }

  let plugins = ['autoresize', 'lists', 'advlist', 'paste']

  if (addLinks) {
    plugins = [...plugins, 'link']
  }

  if (imageEnabled) {
    plugins = [...plugins, 'image']
  }

  return (
    <div {...getRootProps()} className={(onDrag && styles.drop) || null}>
      {isNeedToggleThread && !!value && (
        <TrimButton
          title={t(isTrimmed ? 'HideQuote' : 'ShowQuote')}
          isTrimmed={isTrimmed}
          showTrimmed={() => setIsTrimmed(prevValue => !prevValue)}
        />
      )}

      <Editor
        key={selectKey}
        value={value}
        disabled={disabled}
        ref={editorRef}
        tinymceScriptSrc='https://files.idwell.com/js/tinymce/5.10.8/tinymce.min.js'
        init={{
          auto_focus: autoFocus,
          menubar: false,
          elementpath: false,
          placeholder,
          entity_encoding: 'raw',
          min_height: 200,
          max_height: maxHeight,
          resize,
          object_resizing: imageEnabled,
          autoresize_bottom_margin: 0,
          autoresize_on_init: autoResizeOnInit,
          setup: function (editor) {
            if (imageEnabled) {
              editor.ui.registry.addButton('customImageUploading', {
                icon: 'image',
                tooltip: 'Insert image',
                onAction: () => {
                  handleUpload()
                },
              })
              editor.on('dragover', e => {
                if (e.dataTransfer.types.includes('Files')) {
                  props.setOnDrag(true)
                }
              })
              editor.on('ObjectResized', () => {
                props.onChange(editor.getContent())
              })
            }
          },
          fontsize_formats: '8px 9px 10px 11px 12px 14px 16px',
          textareaName,
          toolbar_groups: {
            alignroup: {
              icon: 'align-left',
              items: 'alignleft aligncenter alignright alignjustify',
            },
          },
          content_style:
            `body {font-family: ${defaultFont.family}; font-size: ${defaultFont.size}px; }` +
            `${
              isNeedToggleThread
                ? 'blockquote blockquote { display: none; }'
                : ''
            }` +
            'p { line-height: 16px; margin: 0; }' +
            'p img { max-width: 100%; }' +
            'a { color: #0000EE; }',
          font_formats:
            'Arial=Arial,sans-serif; Calibri=calibri,sans-serif; Cambria=Cambria; Monospace=monospace; Plus Jakarta Sans=Plus Jakarta Sans,sans-serif; Sans Serif=sans-serif; Serif=serif; Times New Roman=Times New Roman,sans-serif;  Verdana=Verdana,sans-serif',
          plugins,
          toolbar,
          file_picker_types: 'file image media',
          paste_data_images: !!imageEnabled,
          block_unsupported_drop: true,
          automatic_uploads: false,
          link_title: false,
          link_context_toolbar: true,
          target_list: false,
          contextmenu: false,
          language,
        }}
        onBlur={handleBlur}
        onInit={handleInit}
        onFocus={handleFocus}
        onEditorChange={props.onChange}
        onFocusIn={props.setFocused}
        onFocusOut={imageEnabled && handleFocusout}
      />
      <input {...getInputProps()} />
      {onDrag && (
        <div className={styles.editorDrop}>
          <span>{t('DropImageHere')}</span>
        </div>
      )}
    </div>
  )
}

WysiwygEditor.defaultProps = {
  autoResizeOnInit: false,
  showToolbar: true,
  resize: true,
}

export default WysiwygEditor
